/**
 * Holiday art on the backdrop layers (background.ts), painted once into the cached sky and
 * silhouette tiles of a holiday theme (seasonTheme.ts), so it costs nothing per frame:
 *
 *  - Halloween: a big moon instead of the sun, moon or planet; bare trees on the mid layer;
 *    small, dim gravestones and jack-o'-lanterns along the bottom of the near layer, sunk a
 *    little behind the level's ground (over the sea: the moon only).
 *  - Winter: snow on the gentle tops of the mid layer's silhouettes (whatever the theme
 *    draws: roofs, plateaus, hills, cloud tops; not the steep sides of trees and crystals),
 *    a dim frost on the near layer's, found in the tile's own pixels; the sea stays as it is.
 *
 * Everything stays behind the level and well away from the colours of the things the player
 * has to read (orbs, pads, pickups, the light outlines of spikes): the backdrop is scenery
 * only.
 */
import type { Rng } from '@cube-rush/shared';
import { mix, rgba, shade } from './color';
import type { Theme } from './themes';

export type SeasonTilePainter = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rng: Rng,
  theme: Theme,
) => void;

export interface SeasonArt {
  /** Draws the sky's body instead of the theme's (null: the theme's own stays). */
  celestial: ((ctx: CanvasRenderingContext2D, theme: Theme, w: number, h: number) => void) | null;
  /** Painted over the theme's silhouette tiles. */
  mid: SeasonTilePainter | null;
  near: SeasonTilePainter | null;
}

/** The season's art for a holiday theme, null for a plain one. */
export function seasonArt(theme: Theme): SeasonArt | null {
  return theme.season ? ART[theme.season] : null;
}

/** Calls `draw` at x and at x ± w so shapes crossing an edge wrap around (seamless tiles). */
function wrapped(w: number, x: number, extent: number, draw: (x: number) => void): void {
  draw(x);
  if (x - extent < 0) draw(x + w);
  if (x + extent > w) draw(x - w);
}

// ---- Halloween ----

const MOON_LIGHT = '#fff3c4';
const MOON_SHADE = '#f0b45a';
const PUMPKIN = '#d4591c';
const PUMPKIN_DARK = '#8a3410';
const PUMPKIN_FACE = '#ffc24a';

/** A big low moon with a warm glow, a wisp of cloud and a bat crossing it. */
function harvestMoon(ctx: CanvasRenderingContext2D, theme: Theme, w: number, h: number): void {
  const r = Math.min(h * 0.17, w * 0.11);
  const cx = w * 0.72;
  const cy = h * 0.3;
  const glow = ctx.createRadialGradient(cx, cy, r * 0.9, cx, cy, r * 2.6);
  glow.addColorStop(0, rgba('#ffc46b', 0.34));
  glow.addColorStop(0.45, rgba('#ff8a3d', 0.12));
  glow.addColorStop(1, rgba('#ff8a3d', 0));
  ctx.fillStyle = glow;
  ctx.fillRect(cx - r * 2.6, cy - r * 2.6, r * 5.2, r * 5.2);
  const body = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.1, cx, cy, r);
  body.addColorStop(0, MOON_LIGHT);
  body.addColorStop(1, MOON_SHADE);
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = rgba('#c47a2c', 0.28);
  for (const [dx, dy, rr] of [
    [-0.35, -0.2, 0.2],
    [0.28, 0.12, 0.15],
    [-0.05, 0.42, 0.11],
    [0.4, -0.38, 0.08],
  ] as const) {
    ctx.beginPath();
    ctx.arc(cx + dx * r, cy + dy * r, rr * r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Little clouds drifting across its lower half, in the sky's own colour, their tops lit
  // by the moon and their edges soft (painted once into the cached sky, so the blur costs
  // nothing per frame).
  ctx.save();
  ctx.shadowBlur = r * 0.12;
  for (const [dx, dy, width, puff] of [
    [-0.42, 0.5, 1.15, 0.2],
    [0.52, 0.66, 0.9, 0.16],
    [1.18, 0.3, 0.5, 0.11],
  ] as const) {
    const x = cx + dx * r;
    const y = cy + dy * r;
    // The moonlit rim: the same cloud a little higher, lighter; then the cloud over it.
    ctx.fillStyle = rgba(mix(theme.sky[1], MOON_LIGHT, 0.5), 0.8);
    ctx.shadowColor = rgba(theme.sky[1], 0);
    cloud(ctx, x, y - puff * r * 0.16, width * r, puff * r);
    ctx.fillStyle = rgba(theme.sky[1], 0.82);
    ctx.shadowColor = rgba(theme.sky[1], 0.6);
    cloud(ctx, x, y, width * r, puff * r);
  }
  ctx.restore();
  // A bat in front of the moon.
  bat(ctx, cx - r * 0.25, cy - r * 0.12, r * 0.42, 0.6, rgba('#120818', 0.92));
}

/**
 * A small cloud centred at (x, y), `width` across: a flat base with overlapping puffs on it,
 * the biggest (`puff` in radius) in the middle.
 */
function cloud(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  puff: number,
): void {
  ctx.beginPath();
  ctx.ellipse(x, y, width / 2, puff * 0.42, 0, 0, Math.PI * 2);
  const puffs = 4;
  for (let i = 0; i < puffs; i++) {
    const t = (i + 0.5) / puffs;
    const r = puff * (0.55 + 0.45 * Math.sin(Math.PI * t));
    const px = x + (t - 0.5) * width * 0.8;
    const py = y - puff * 0.25 - r * 0.25;
    ctx.moveTo(px + r, py);
    ctx.arc(px, py, r, 0, Math.PI * 2);
  }
  ctx.fill();
}

/** A bat silhouette centred at (x, y), `span` wide; `lift` −1 (wings down) … 1 (up). */
export function bat(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  span: number,
  lift: number,
  color: string,
  rim: string | null = null,
): void {
  const s = span / 2;
  const tip = -lift * s * 0.55;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, y - s * 0.12);
  // Left wing: up to the tip, then scalloped back to the body.
  ctx.quadraticCurveTo(x - s * 0.45, y - s * 0.32 + tip * 0.5, x - s, y + tip);
  ctx.quadraticCurveTo(
    x - s * 0.82,
    y + s * 0.02 + tip * 0.4,
    x - s * 0.66,
    y + s * 0.12 + tip * 0.35,
  );
  ctx.quadraticCurveTo(
    x - s * 0.5,
    y + s * 0.02 + tip * 0.2,
    x - s * 0.36,
    y + s * 0.16 + tip * 0.15,
  );
  ctx.quadraticCurveTo(x - s * 0.24, y + s * 0.08, x - s * 0.12, y + s * 0.2);
  // Body and ears.
  ctx.lineTo(x, y + s * 0.3);
  ctx.lineTo(x + s * 0.12, y + s * 0.2);
  ctx.quadraticCurveTo(x + s * 0.24, y + s * 0.08, x + s * 0.36, y + s * 0.16 + tip * 0.15);
  ctx.quadraticCurveTo(
    x + s * 0.5,
    y + s * 0.02 + tip * 0.2,
    x + s * 0.66,
    y + s * 0.12 + tip * 0.35,
  );
  ctx.quadraticCurveTo(x + s * 0.82, y + s * 0.02 + tip * 0.4, x + s, y + tip);
  ctx.quadraticCurveTo(x + s * 0.45, y - s * 0.32 + tip * 0.5, x, y - s * 0.12);
  ctx.moveTo(x - s * 0.1, y - s * 0.08);
  ctx.lineTo(x - s * 0.06, y - s * 0.24);
  ctx.lineTo(x, y - s * 0.12);
  ctx.lineTo(x + s * 0.06, y - s * 0.24);
  ctx.lineTo(x + s * 0.1, y - s * 0.08);
  ctx.fill();
  if (rim) {
    // Moonlight on the edges: a dark bat on a dark sky still shows.
    ctx.strokeStyle = rim;
    ctx.lineWidth = Math.max(1, span * 0.035);
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
}

/** Bare, crooked trees standing on the strip bottom. */
function bareTrees(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  const color = shade(t.mid, -0.45);
  const count = 3;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  for (let i = 0; i < count; i++) {
    const x = (i + rng.range(0.15, 0.85)) * (w / count);
    const height = h * rng.range(0.5, 0.78);
    const width = Math.max(2, height * 0.07);
    const lean = rng.range(-0.15, 0.15);
    const seed = rng.int(0, 1_000_000);
    wrapped(w, x, height * 0.7, (cx) => {
      branch(ctx, cx, h + width, -Math.PI / 2 + lean, height * 0.45, width, 4, seed);
    });
  }
}

/** One branch and, recursively, the two it splits into (a tiny deterministic noise source). */
function branch(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  length: number,
  width: number,
  depth: number,
  seed: number,
): void {
  const wobble = (k: number) => ((((seed * 9301 + k * 49297) % 233280) + 233280) % 233280) / 233280;
  const bend = (wobble(depth) - 0.5) * 0.5;
  const x2 = x + Math.cos(angle + bend) * length;
  const y2 = y + Math.sin(angle + bend) * length;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.quadraticCurveTo(
    x + Math.cos(angle) * length * 0.5 + bend * length * 0.3,
    y + Math.sin(angle) * length * 0.5,
    x2,
    y2,
  );
  ctx.stroke();
  if (depth <= 0) return;
  const spread = 0.45 + wobble(depth + 7) * 0.35;
  const next = depth - 1;
  branch(ctx, x2, y2, angle - spread, length * 0.68, width * 0.62, next, seed + 1);
  branch(ctx, x2, y2, angle + spread * 0.8, length * 0.6, width * 0.6, next, seed + 2);
}

/**
 * Gravestones and glowing jack-o'-lanterns along the bottom of the near strip: a far
 * graveyard, not props next to the spikes and pads standing on the floor line right in front
 * of it. So they are small and dim, close to the strip's own colour, and sunk a little below
 * its bottom (the floor line when the camera rests: the ground hides their feet).
 */
function graveyard(ctx: CanvasRenderingContext2D, w: number, h: number, rng: Rng, t: Theme): void {
  const stone = mix(t.near, '#8d84a8', 0.13);
  const rim = rgba('#e4dcff', 0.1);
  // The pumpkins first: the stones stand in front of some.
  const pumpkins = 3;
  for (let i = 0; i < pumpkins; i++) {
    const x = (i + rng.range(0.1, 0.9)) * (w / pumpkins);
    const size = h * rng.range(0.055, 0.075);
    wrapped(w, x, size * 2, (cx) => jackOLantern(ctx, cx, h + size * 0.3, size, t.near));
  }
  const count = 6;
  for (let i = 0; i < count; i++) {
    const x = (i + rng.range(0.2, 0.8)) * (w / count);
    const sh = h * rng.range(0.17, 0.27);
    const sw = sh * rng.range(0.55, 0.75);
    const kind = rng.int(0, 2);
    const tilt = rng.range(-0.12, 0.12);
    const base = h + sh * 0.15;
    wrapped(w, x, sw, (cx) => gravestone(ctx, cx, base, sw, sh, kind, tilt, stone, rim, t.near));
  }
}

function gravestone(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  w: number,
  h: number,
  kind: number,
  tilt: number,
  fill: string,
  rim: string,
  ink: string,
): void {
  ctx.save();
  ctx.translate(x, base);
  ctx.rotate(tilt);
  ctx.beginPath();
  if (kind === 0) {
    // A rounded slab.
    ctx.moveTo(-w / 2, 0);
    ctx.lineTo(-w / 2, -h + w / 2);
    ctx.arc(0, -h + w / 2, w / 2, Math.PI, 0);
    ctx.lineTo(w / 2, 0);
  } else if (kind === 1) {
    // A cross.
    const arm = w * 0.32;
    ctx.rect(-arm / 2, -h, arm, h);
    ctx.rect(-w / 2, -h * 0.72, w, arm);
  } else {
    // A square slab with a pointed top.
    ctx.moveTo(-w / 2, 0);
    ctx.lineTo(-w / 2, -h * 0.8);
    ctx.lineTo(0, -h);
    ctx.lineTo(w / 2, -h * 0.8);
    ctx.lineTo(w / 2, 0);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = rim;
  ctx.lineWidth = Math.max(1, w * 0.06);
  ctx.stroke();
  if (kind !== 1) {
    // An engraved cross.
    ctx.strokeStyle = rgba(ink, 0.75);
    ctx.lineWidth = Math.max(1, w * 0.07);
    ctx.beginPath();
    ctx.moveTo(0, -h * 0.72);
    ctx.lineTo(0, -h * 0.36);
    ctx.moveTo(-w * 0.16, -h * 0.6);
    ctx.lineTo(w * 0.16, -h * 0.6);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * A small carved pumpkin sitting on `base`, glowing softly (painted once into the tile), its
 * colours a third of the way into the `dusk` around it: scenery, not a pad or a pickup.
 */
function jackOLantern(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  size: number,
  dusk: string,
): void {
  const r = size;
  const cy = base - r * 0.82;
  const glow = ctx.createRadialGradient(x, cy, r * 0.4, x, cy, r * 2.2);
  glow.addColorStop(0, rgba('#ffb238', 0.12));
  glow.addColorStop(1, rgba('#ffb238', 0));
  ctx.fillStyle = glow;
  ctx.fillRect(x - r * 2.2, cy - r * 2.2, r * 4.4, r * 4.4);
  // Body: three overlapping lobes.
  ctx.fillStyle = mix(PUMPKIN, dusk, 0.35);
  ctx.beginPath();
  ctx.ellipse(x - r * 0.42, cy, r * 0.62, r * 0.8, 0, 0, Math.PI * 2);
  ctx.ellipse(x + r * 0.42, cy, r * 0.62, r * 0.8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = mix(shade(PUMPKIN, 0.08), dusk, 0.35);
  ctx.beginPath();
  ctx.ellipse(x, cy, r * 0.6, r * 0.84, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = mix(PUMPKIN_DARK, dusk, 0.35);
  ctx.lineWidth = Math.max(1, r * 0.09);
  ctx.beginPath();
  ctx.ellipse(x, cy, r * 0.6, r * 0.84, 0, 0, Math.PI * 2);
  ctx.stroke();
  // Stem.
  ctx.fillStyle = mix('#3b5a1a', dusk, 0.35);
  ctx.fillRect(x - r * 0.1, cy - r * 1.05, r * 0.2, r * 0.3);
  // The glowing face.
  ctx.fillStyle = mix(PUMPKIN_FACE, dusk, 0.3);
  ctx.beginPath();
  for (const side of [-1, 1]) {
    ctx.moveTo(x + side * r * 0.5, cy - r * 0.08);
    ctx.lineTo(x + side * r * 0.16, cy - r * 0.08);
    ctx.lineTo(x + side * r * 0.33, cy - r * 0.38);
    ctx.closePath();
  }
  ctx.moveTo(x - r * 0.55, cy + r * 0.18);
  ctx.lineTo(x - r * 0.2, cy + r * 0.28);
  ctx.lineTo(x - r * 0.12, cy + r * 0.18);
  ctx.lineTo(x + r * 0.12, cy + r * 0.18);
  ctx.lineTo(x + r * 0.2, cy + r * 0.28);
  ctx.lineTo(x + r * 0.55, cy + r * 0.18);
  ctx.quadraticCurveTo(x, cy + r * 0.78, x - r * 0.55, cy + r * 0.18);
  ctx.fill();
}

// ---- winter ----

/** A tile's pixels as getImageData gives them: RGBA (not premultiplied), row by row. */
export interface TilePixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

type Rgb = readonly [number, number, number];

/** How the snow on a layer's silhouettes looks (SNOW, FROST). */
export interface SnowLook {
  /** Colour at the top of a cap and at its lower edge. */
  top: Rgb;
  bottom: Rgb;
  /** Share of the silhouette's own colour in the snow's: 0 = the colours above, 0.5 = half-way. */
  dim: number;
  /**
   * The snow line, as shares of the tile's height above its bottom: no snow below the first
   * (the tile's bottom is the floor line when the camera rests: low ground there is right
   * behind the spikes standing on the floor), thickening to full at the second.
   */
  line: readonly [number, number];
}

/** White snow on the mid layer's silhouettes, high above the floor (the strip is 0.52 view tall). */
export const SNOW: SnowLook = {
  top: [240, 247, 255],
  bottom: [206, 225, 255],
  dim: 0,
  line: [0.24, 0.4],
};
/**
 * A dim pale-blue frost on the near layer's (0.3 view tall), half-way to their own colour and
 * on its high ground only: right behind the level, white would draw the same light outlines
 * as the spikes standing there.
 */
export const FROST: SnowLook = {
  top: [196, 220, 246],
  bottom: [170, 198, 234],
  dim: 0.5,
  line: [0.42, 0.66],
};

/**
 * How steep a ridge may be for snow to lie on it (rise per run, px per px): in full up to
 * GENTLE (roofs, plateaus, gentle hills, cloud tops), thinning out to none at STEEP (the sides
 * of trees and crystals, sharp peaks): a white outline along every edge would look like rows of
 * spikes behind the real ones.
 */
const GENTLE = 0.35;
const STEEP = 0.85;

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Snow on top of whatever a tile shows: where a silhouette's top edge is gentle and above
 * the look's snow line, its topmost pixels turn to snow for a few pixels down (inside the
 * silhouette, so nothing grows); it thins out on steeper edges and towards the snow line, and
 * stays off steep edges and low ground. Snow lies on silhouettes only: not on lights (a lit
 * window, lava, a glowing crater, a peak's own snow) and not on anything see-through (smoke,
 * a glow). Pure: changes `pixels` in place.
 */
export function snowOnSilhouettes(pixels: TilePixels, look: SnowLook = SNOW): void {
  const { data, width: w, height: h } = pixels;
  if (w <= 0 || h <= 0) return;
  const alpha = (x: number, y: number) => data[(y * w + x) * 4 + 3]!;
  const brightness = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    return data[i]! + data[i + 1]! + data[i + 2]!;
  };
  const depth = Math.max(2, h * 0.04);
  /** Opaque enough to be a silhouette's edge (a rim stroked along it included). */
  const solid = 170;
  /** A silhouette is fully opaque this far under its edge; smoke and glow never are. */
  const body = Math.max(2, Math.round(h * 0.012));
  /** Sum of r, g and b of the lights. */
  const bright = 480;
  /** How far to either side the rise of a ridge is measured, px. */
  const reach = Math.max(2, Math.round(h * 0.025));
  // Where every column's silhouette begins (h: none there), and whether a light is there.
  const tops = new Int32Array(w);
  const lit = new Uint8Array(w);
  /** The tops averaged over a few columns: the steepness of a pixel staircase without its steps. */
  const smooth = new Float64Array(w);
  for (let x = 0; x < w; x++) {
    let top = h;
    for (let y = 0; y < h; y++) {
      if (alpha(x, y) >= solid && alpha(x, Math.min(h - 1, y + body)) >= 250) {
        top = y;
        break;
      }
    }
    tops[x] = top;
    // A light stays as it is (a bright rim stroked along a dark ridge is no light: it is the
    // ridge's edge, and the snow covers it).
    const under = Math.min(h - 1, top + body);
    if (top < h && brightness(x, top) > bright && brightness(x, under) > bright) lit[x] = 1;
  }
  // Tiles repeat side by side: the edge columns look across to the other edge.
  const at = (x: number) => (((x % w) + w) % w) | 0;
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let k = -2; k <= 2; k++) sum += tops[at(x + k)]!;
    smooth[x] = sum / 5;
  }
  for (let x = 0; x < w; x++) {
    const top = tops[x]!;
    if (top >= h || lit[x]) continue;
    // The steeper side of the ridge here (a column without a silhouette is a cliff).
    const here = smooth[x]!;
    const rise =
      Math.max(Math.abs(here - smooth[at(x - reach)]!), Math.abs(smooth[at(x + reach)]! - here)) /
      reach;
    const amount =
      (1 - smoothstep(GENTLE, STEEP, rise)) * smoothstep(look.line[0], look.line[1], 1 - top / h);
    if (amount <= 0) continue;
    // Lumpy: the cap is thicker and thinner along the ridge, thinner on a slope.
    const lumps = 0.75 + 0.25 * Math.sin(x * 0.11) + 0.15 * Math.sin(x * 0.037 + 1.3);
    const d = depth * lumps * amount;
    const end = Math.min(h, top + d);
    // The silhouette's own colour, under its edge (and under a rim stroked along it).
    const own = (Math.min(h - 1, top + body) * w + x) * 4;
    const ownR = data[own]!;
    const ownG = data[own + 1]!;
    const ownB = data[own + 2]!;
    for (let y = top; y < end; y++) {
      const i = (y * w + x) * 4;
      if (data[i + 3]! < solid) break;
      const k = (y - top) / d;
      // The look's colour down the cap, dimmed towards the silhouette's, laid over the pixel
      // as much as the slope lets the snow lie.
      const r = look.top[0] + (look.bottom[0] - look.top[0]) * k;
      const g = look.top[1] + (look.bottom[1] - look.top[1]) * k;
      const b = look.top[2] + (look.bottom[2] - look.top[2]) * k;
      data[i] = Math.round(data[i]! + (r + (ownR - r) * look.dim - data[i]!) * amount);
      data[i + 1] = Math.round(data[i + 1]! + (g + (ownG - g) * look.dim - data[i + 1]!) * amount);
      data[i + 2] = Math.round(data[i + 2]! + (b + (ownB - b) * look.dim - data[i + 2]!) * amount);
      data[i + 3] = Math.round(data[i + 3]! + (255 - data[i + 3]!) * amount);
    }
  }
}

/** Winter on a silhouette tile (read back once per tile); the sea stays as it is. */
function snowTops(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  theme: Theme,
  look: SnowLook,
): void {
  if (theme.backdrop === 'ocean') return;
  let image: ImageData;
  try {
    image = ctx.getImageData(0, 0, w, h);
  } catch {
    return;
  }
  snowOnSilhouettes(image, look);
  ctx.putImageData(image, 0, 0);
}

/** A painter for land only: over the sea there is nothing to stand on (bats and moon only). */
function onLand(painter: SeasonTilePainter): SeasonTilePainter {
  return (ctx, w, h, rng, theme) => {
    if (theme.backdrop !== 'ocean') painter(ctx, w, h, rng, theme);
  };
}

const ART: Readonly<Record<'halloween' | 'winter', SeasonArt>> = {
  halloween: {
    celestial: harvestMoon,
    mid: onLand(bareTrees),
    near: onLand(graveyard),
  },
  winter: {
    celestial: null,
    mid: (ctx, w, h, _rng, theme) => snowTops(ctx, w, h, theme, SNOW),
    near: (ctx, w, h, _rng, theme) => snowTops(ctx, w, h, theme, FROST),
  },
};
